import * as React from 'react';
import { BLOCKS, Document, INLINES, Node } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Heading1, Heading4 } from '@blueprint/heading';
import { Divider } from '@blueprint/divider';
// import { OrderedList } from '@blueprint/list';
import { removeSearchIdentifierFromLinkNode } from './functions/removeSearchIdentifierFromLinkNode';
import { renderHeadingOrJumpLink } from './functions/renderHeadingOrJumpLink';
import { renderMarkup } from './functions/helpers';
import EmbeddedEntry from './components/EmbeddedEntry';
import EntryHyperlink from './components/EntryHyperlink';
import Hyperlink from './components/Hyperlink';
import { BlockQuote } from '@zoopla-eng/zds-components/quote';
import { styled } from '@zoopla-eng/zds-components/themes';
import { Bulleted as List } from '@zoopla-eng/zds-components/lists/bulleted';
import { Link } from '@zoopla-eng/zds-components/link';

const Text = styled('p', {
    marginBottom: '$S400',
    fontGroup: 'ladle',
    color: '$textBody'
});

const Bulleted = styled(List, {
    '& li': {
        fontGroup: 'ladle'
    }
});

const OrderedList = styled('ol', {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column',
    gap: '$S200',
    fontGroup: 'ladle',
    color: '$textBody',
    listStyle: 'none',
    counterReset: 'counter 0',
    '& li': {
        display: 'flex',
        counterIncrement: 'counter',
        '&:before': {
            width: '20px',
            marginRight: '$S300',
            content: 'counter(counter) "."'
        }
    }
});

// Defines commonly shared Contentful rich text options used
// when rendering rich text fields
// e.g. shared between Article rich text and Author Bio rich text fields
const OPTIONS = {
    [BLOCKS.HEADING_1]: (_node: Node, children: React.ReactNode) => (
        <Heading1>{children}</Heading1>
    ),
    [BLOCKS.HEADING_2]: (_node: Node, children: React.ReactNode) => {
        return renderHeadingOrJumpLink(_node, children);
    },
    [BLOCKS.HEADING_3]: (_node: Node, children: React.ReactNode) => {
        return renderHeadingOrJumpLink(_node, children);
    },
    [BLOCKS.HEADING_4]: (_node: Node, children: React.ReactNode) => (
        <Heading4>{children}</Heading4>
    ),
    [BLOCKS.PARAGRAPH]: (_node: Node, children: React.ReactNode) =>
        children && <Text>{children}</Text>,
    [BLOCKS.UL_LIST]: (_node: React.ReactNode, children: React.ReactNode) => (
        <Bulleted>{children}</Bulleted>
    ),
    [BLOCKS.OL_LIST]: (_node: React.ReactNode, children: React.ReactNode) => (
        <OrderedList>{children}</OrderedList>
    ),
    [BLOCKS.QUOTE]: (_node: Node, children: React.ReactNode) =>
        documentToReactComponents(_node as Document, {
            renderNode: {
                [BLOCKS.QUOTE]: (_node, children) => (
                    <BlockQuote>{children}</BlockQuote>
                )
            },
            renderText: (text: string) => {
                return text
                    .split('\n')
                    .reduce(
                        (
                            children: string | React.ReactNode[],
                            textSegment: string,
                            index: number
                        ) => {
                            return [
                                ...children,
                                index > 0 && <br key={index} />,
                                textSegment
                            ];
                        },
                        []
                    );
            }
        }),
    [INLINES.HYPERLINK]: (node: Node) => (
        <Hyperlink nodeData={removeSearchIdentifierFromLinkNode(node)} />
    ),
    [BLOCKS.HR]: () => <Divider variant="horizontal" />,
    [INLINES.ENTRY_HYPERLINK]: (node: Node) => (
        <EntryHyperlink nodeData={node} />
    ),
    [BLOCKS.EMBEDDED_ENTRY]: (node: Node) => {
        return <EmbeddedEntry nodeData={node} />;
    },
    [INLINES.ASSET_HYPERLINK]: (node: Node, children: React.ReactNode) => {
        let url = '';
        if (node.data.target.fields) {
            url = node.data.target.fields.file.url;
        } else {
            // We throw a warning just in case the user is in preview mode
            // which means the asset might not yet be published (because preview mode)
            // and we don't want to break the page
            console.warn(
                'An asset hyperlink is undefined -- has the asset been published?'
            );
        }
        return <Link href={url}>{children}</Link>;
    }
};

export const CONTENTFUL_RICH_TEXT_OPTIONS = {
    renderNode: {
        ...OPTIONS
    },
    renderMarkup
};
